import { Button, DatePicker, message, Popover, Switch } from 'antd'
import { useEffect, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useParams } from 'react-router-dom'
import { useStatsSingleProject } from '../../../hooks/useStatsSingleProject'
import { Analytics, Site } from '../../../Types'
import {
  getExpectedRandoesProjectionSeries,
  getExpectedScreeningsProjectionSeries,
  getGoalRandoProjectionSeries,
  getGoalScreeningsProjectionSeries,
  SeriesForPlot,
} from '../utils'
import { formatDateToDDMMMYYYY } from '../visits/CompletedVisits'
import { SettingOutlined } from '@ant-design/icons'

export const FunnelTimelineChart = () => {
  const { projectId } = useParams() as { projectId: string }
  const { timelineChartData, newestGoal, sites, projectAnalytics, expectedRandomizations, runInPeriod } =
    useStatsSingleProject(projectId)
  const [showSiteGreenlightAnnotations, setShowSiteGreenlightAnnotations] = useState<boolean>(false)
  /* const [showAdSpendLine, setShowAdSpendLine] = useState<boolean>(false)
  const [showCumulative, setShowCumulative] = useState<boolean>(true) */
  const [showOldRandoGoals, setShowOldRandoGoals] = useState<boolean>(false)
  const [showDatalabelLegend, setShowDatalabelLegend] = useState<boolean>(false)

  const [showGoalAnnotations, setShowGoalAnnotations] = useState<boolean>(true)

  const [startDateTime, setStartDateTime] = useState<undefined | number>(undefined)
  const [endDateTime, setEndDateTime] = useState<undefined | number>(undefined)

  const enrolledSeries = timelineChartData.SUBJECT_RANDOMISED

  const oldRandoGoalsPlot = showOldRandoGoals ? getOldRandomizationGoalsLines(projectAnalytics) : []

  const projectedEnrollmentsTitle = 'Enrollments forecasted'
  const screenGoalTitle = 'Screening goal'

  /* const expectedRandomizations = {
    dateTime: new Date('27 Jun 2025').getTime(),
    count: 32,
  } */
  useEffect(() => {
    if (!projectAnalytics?.expectedScreenFailRate) {
      message.warning('No expected-screen-fail-rate set in Admin - using 0.25')
    }
  }, [projectAnalytics])

  const expectedScreenFailureRate = projectAnalytics?.expectedScreenFailRate ?? 0.25
  const goalScreenings = Math.round((newestGoal?.totalRandomizations ?? 0) / (1 - expectedScreenFailureRate))

  const series = [
    {
      color: '#2E93fA',
      name: 'Signed Up',
      data: timelineChartData.SIGNED_UP,
      hidden: true,
    },
    {
      color: '#c7b9ab',
      name: 'Prescreened Eligible',
      data: timelineChartData.ASSIGNED_TO_SITE, // new referrals are always also prescreened.
      hidden: true,
    },
    /* {
        color: '#4a6fa5',
        name: 'Assigned to site',
        data: getSeriesByEventType(data, [EventType.ReadyForPhoneScreen]),
      }, */
    {
      color: '#00766f', //the color of mark as qualified button.
      name: 'Phonescreened Eligible',
      data: timelineChartData.NEW_REFERRAL,
      hidden: true,
    },
    {
      color: '#ca0eda',
      name: 'Scheduled for screening',
      data: timelineChartData.SCHEDULED_FOR_SCREENING,
      hidden: true,
    },
    {
      color: '#949494',
      name: 'Screened',
      data: timelineChartData.SIGNED_CONSENT,
    },
    {
      color: '#949494',
      name: 'Screenings forecasted',
      data: getExpectedScreeningsProjectionSeries(
        timelineChartData.SIGNED_CONSENT,
        runInPeriod,
        expectedRandomizations
      ),
      type: 'line',
      zIndex: 3,
      dash: 3,
      legendShape: 'line',
    },
    {
      color: '#636363',
      name: screenGoalTitle,
      // randoGoal / expected screenFailure Rate
      data: getGoalScreeningsProjectionSeries(projectAnalytics, newestGoal, runInPeriod, goalScreenings),
      type: 'line',
      zIndex: 0,
      dash: 10,
      width: 1,
      legendShape: 'line',
    },
    {
      color: '#8b5cf6',
      name: 'Scheduled for randomization',
      data: timelineChartData.SCHEDULED_FOR_RANDOMISATION,
      hidden: true,
    },
    {
      color: '#DAA520',
      name: 'Enrolled',
      data: enrolledSeries,
    },
    {
      color: '#DAA520',
      name: projectedEnrollmentsTitle,
      data: getExpectedRandoesProjectionSeries(enrolledSeries, expectedRandomizations),
      type: 'line',
      zIndex: 3,
      dash: 3,
      legendShape: 'line',
    },
    {
      color: '#9e833e',
      name: 'Enrollment goal',
      data: getGoalRandoProjectionSeries(projectAnalytics, newestGoal, runInPeriod),
      type: 'line',
      zIndex: 0,
      dash: 10,
      width: 1,
      legendShape: 'line',
    },

    ...oldRandoGoalsPlot,
  ]

  const options = {
    chart: {
      id: 'area-datetime',
      type: 'area',
      height: 350,
      /* zoom: {
          autoScaleYaxis: true,
        }, */
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    annotations: {
      yaxis: showGoalAnnotations
        ? [
            newestGoal?.totalRandomizations
              ? {
                  y: newestGoal.totalRandomizations,
                  borderColor: '#DAA520',
                  strokeDashArray: 0,
                  label: {
                    show: true,
                    text: `Rando Goal: ${newestGoal.totalRandomizations}${
                      newestGoal.goalType === 'Or more' ? '+' : ''
                    }`,
                    style: {
                      color: '#fff',
                      background: '#DAA520',
                    },
                  },
                }
              : {},
            newestGoal?.totalRandomizations
              ? {
                  y: goalScreenings,
                  borderColor: '#949494',
                  strokeDashArray: 0,
                  label: {
                    show: true,
                    text: `Screen Goal: ${goalScreenings}`,
                    style: {
                      color: '#fff',
                      background: '#949494',
                    },
                  },
                }
              : {},
          ]
        : [],
      xaxis: showSiteGreenlightAnnotations ? getSiteGreenlightAnnotations(sites) : [],
      /* points: [
          {
            x: expectedRandomizations.dateTime,
            y: expectedRandomizations.count,
            marker: {
              size: 6,
              fillColor: '#fff',
              strokeColor: '#2698FF',
              radius: 2,
            },
            label: {
              borderColor: '#FF4560',
              offsetY: 0,
              style: {
                color: '#fff',
                background: '#FF4560',
              },

              text: `If ad-stop (${expectedRandomizations.count})`,
            },
          },
        ], */
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1, 2, 3, 4, 5, 9, 7, 8],
      formatter: function (val, opts) {
        // Check if the current data point is the last one in the series
        if (opts.dataPointIndex === opts.w.globals.series[opts.seriesIndex].length - 1) {
          const serie = series[opts.seriesIndex]
          const legend = serie?.name
          const label = showDatalabelLegend ? `${val} ${legend}` : val
          return label
        } else {
          return ''
        }
      },
    },
    markers: {
      size: 0,
      style: 'hollow',
    },
    legend: {
      show: true,
      position: 'top',
      markers: {
        shape: series.map((serie) => ('legendShape' in serie && serie.legendShape ? serie.legendShape : 'circle')),
      },
    },
    xaxis: {
      type: 'datetime',
      //min: new Date('01 Jan 2024').getTime(),
      //max: newestGoal?.deadline && !showOldRandoGoals ? new Date(newestGoal.deadline).getTime() : Date.now(),
      tickAmount: 6,
      ...(startDateTime !== undefined ? { min: startDateTime } : {}),
      ...(endDateTime !== undefined ? { max: endDateTime } : {}),
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy',
      },
      y: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const seriesName = w.globals.seriesNames[seriesIndex]

          if (seriesName === screenGoalTitle) {
            return `<span>
                  Screen goal: ${value}
                  <span class="custom-tooltip-projected">
                  <br />
                  randomization-goal / (1 - expected-screen-failure-rate)<br/>
             = ${newestGoal?.totalRandomizations} / (1 - ${expectedScreenFailureRate}) = ${value}
                  </span>
                </span>`
          }

          if (seriesName === projectedEnrollmentsTitle) {
            return `<p class="no-line-height">
                  Enrollments forecasted: ${value}
                  <span class="custom-tooltip-projected">
                  <br />
                  If ads stops, we expect this amount of enrollments.
                  <br/>
                  <br />
                  <u>Calculation:</u>
                  <br />
                  Randomized to date: <b>${expectedRandomizations.currentlyEnrolled}</b>
                  <br /> Expected future randomizations: <b>${Math.round(
                    expectedRandomizations.passesRandomization
                  )}</b> (
                  ${formatDateToDDMMMYYYY(expectedRandomizations.expectedEnddate)})<br />
                  (based on number of Responders currently waiting in each state and successrate for each state)
                  <br />
                  <br />
                  Assumptions about how many days a Responder is in each state:
                  <br />
                  Ready for phonescreening: 5 days
                  <br />
                  Qualified lead: 14 days
                  <br />
                  Screening: 21 (or runInPeriod from Admin module)
                  <br />
                  </span>
                </p>`
          }

          return value
        },
        title: {
          formatter: (value, { seriesIndex, dataPointIndex, w }) => {
            const seriesName = w.globals.seriesNames[seriesIndex]

            if (seriesName === screenGoalTitle) {
              return ``
            }

            if (seriesName === projectedEnrollmentsTitle) {
              return ``
            }

            return value
          },
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    title: {
      text: 'Timeline',
      align: 'left',
    },
    stroke: {
      width: series.map((serie) => ('width' in serie && serie.width ? serie.width : 4)),
      curve: 'straight',
      dashArray: series.map((serie) => ('dash' in serie && serie.dash ? serie.dash : 0)),
    },
  }

  return (
    <div>
      <div className="chart">
        <div id="chart-timeline" style={{ position: 'relative' }}>
          {/*@ts-ignore */}
          <ReactApexChart options={options} series={series} type="area" height={350} />
          <div style={{ position: 'absolute', top: -5, right: 5, zIndex: 10 }}>
            <Popover
              content={
                <div>
                  <div>
                    <Switch
                      checked={showSiteGreenlightAnnotations}
                      onChange={(val) => setShowSiteGreenlightAnnotations(val)}
                      size="small"
                      checkedChildren="Showing site greenlights"
                      unCheckedChildren="Hiding site greenlights"
                    />
                  </div>
                  <div>
                    <Switch
                      checked={showOldRandoGoals}
                      onChange={(val) => setShowOldRandoGoals(val)}
                      size="small"
                      checkedChildren="Showing old randomization goals"
                      unCheckedChildren="Hiding old randomization goals"
                    />
                  </div>
                  <div>
                    <Switch
                      checked={showDatalabelLegend}
                      onChange={(val) => setShowDatalabelLegend(val)}
                      size="small"
                      checkedChildren="Showing legend in data-label"
                      unCheckedChildren="Legend not in data-label"
                    />
                  </div>
                  <div>
                    <Switch
                      checked={showGoalAnnotations}
                      onChange={(val) => setShowGoalAnnotations(val)}
                      size="small"
                      checkedChildren="Showing goal annotations"
                      unCheckedChildren="Hiding goal annotations"
                    />
                  </div>
                  <div className="mt-2">
                    <DatePicker
                      placeholder="Start date"
                      allowClear={false}
                      onChange={(val, dateString) => {
                        if (dateString) {
                          setStartDateTime(new Date(dateString).getTime())
                        } else {
                          setStartDateTime(undefined)
                        }
                      }}
                    />
                    <DatePicker
                      placeholder="End date"
                      allowClear={false}
                      onChange={(val, dateString) => {
                        if (dateString) {
                          setEndDateTime(new Date(dateString).getTime())
                        } else {
                          setEndDateTime(undefined)
                        }
                      }}
                    />
                  </div>
                </div>
              }
              title="Display Settings"
              trigger="click"
            >
              <Button shape="circle" icon={<SettingOutlined />} />
            </Popover>
          </div>
        </div>
      </div>
      <div id="html-dist"></div>
    </div>
  )
}

function getSiteGreenlightAnnotations(sites: Site[]) {
  if (!sites) return []

  const sitesWithGreenLight = sites.filter((site) => site.greenlight)

  sitesWithGreenLight.sort((a, b) => {
    return new Date(a.greenlight!).getTime() - new Date(b.greenlight!).getTime()
  })

  const accumulatedSites: { [greenlight: string]: number } = {}

  sitesWithGreenLight.forEach((site, index) => {
    accumulatedSites[site.greenlight!] = index + 1
  })

  const annotations: any[] = []

  for (const [greenlight, accCount] of Object.entries(accumulatedSites)) {
    annotations.push({
      x: new Date(greenlight).getTime(),
      borderColor: '#999',
      yAxisIndex: 0,
      label: {
        show: true,
        text: `${accCount} ${accCount === 1 ? 'site ' : 'sites'}`,
        style: {
          color: '#fff',
          background: '#775DD0',
        },
      },
    })
  }

  console.log('annotations:', { sites, sitesWithGreenLight, accumulatedSites, annotations })

  return annotations
}

type SeriesWithMeta = {
  /** hexcode. */
  color?: string
  /** @example "signed up" */
  name: string
  data: SeriesForPlot
  type?: string
}
/** Gives us a line for each rando goal where the line is from effective date to deadline date. */
function getOldRandomizationGoalsLines(projectAnalytics: Analytics | undefined): SeriesWithMeta[] {
  if (!projectAnalytics?.goals) return []

  const allPlotData: SeriesWithMeta[] = []
  let i = 1

  for (const goal of projectAnalytics.goals) {
    const goalSeries: SeriesForPlot = []
    goalSeries.push([goal.effectiveDate, goal.totalRandomizations])
    goalSeries.push([goal.deadline, goal.totalRandomizations])

    allPlotData.push({
      data: goalSeries,
      name: `Goal${i}${goal.goalType === 'Or more' ? '+' : ''}`,
      color: '#DAA520',
      //type: 'line',
    })
    i++
  }

  return allPlotData
}
